import { useEffect, useState, useContext, useRef, useCallback } from 'react';
import cx from 'classnames';
import { useFormatter, useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import Link from 'next/link';
import CommonImage from '@/components/CommonImage';
import CommonEquipment from '../CommonEquipment';
import { getDetailUrl } from '@/constants/url';
import ThreadTitle from '@/components/ThreadTitle/mIndex';
import {
  getThreadRelated,
  IThreadRelated,
} from '@/api/detail';
import formatDateHandler from '@/utils/formatDate';
import { formatDisplayNumber } from '@/utils/formatNumber';
import { isInViewPort } from '@/utils/isInViewPort';
import { isStoreAppBannerEntry } from '@/utils/url';
import Styles from './mIndex.module.scss';
import { StoreContext, namespace } from '@/context';
import { ThreadDetailCtx } from '@/context/threadDetail';
import { getOptimizedImageUrl } from '@/utils/image';


const noAvatarImageUrl = `${process.env.RESOURCE_URL}uc_server/images/noavatar_middle.gif`;

interface IThreadBlockItem {
  tid: string;
  subject: string;
  /** 禁用相关推荐设备点击 */
  disableDeviceClick?: boolean
}
const ThreadBlockItem = ({
  tid,
  subject,
  disableDeviceClick,
}: IThreadBlockItem) => {
  const router = useRouter();

  const [relatedItems, setRelatedItems] = useState<IThreadRelated[] | null>(null);

  const t = useTranslations();
  const format = useFormatter();

  const { state } = useContext(StoreContext);
  const { track, TRACK_EVENTS, extractThreadTags } = state[namespace.app].sensors || {};
  const threadDetail = useContext(ThreadDetailCtx);
  const domRef = useRef<HTMLParagraphElement>(null);
  const isEmit = useRef(false);

  const scrollToViewHandler  = useCallback(() => {
    if(!domRef.current || !track) return;

    const bottom = domRef.current.getBoundingClientRect().bottom;
    const footerHeight = document.getElementById("bottom-comment")?.offsetHeight || 0;
    const viewHeight = document.body.clientHeight;
    const isVisi = bottom  <= viewHeight - footerHeight;
    if(isVisi && !isEmit.current){
      isEmit.current = true;
      const container = document.getElementById("__next");
      container?.removeEventListener('scroll', scrollToViewHandler);
      // 帖子相关推荐曝光
      track?.(TRACK_EVENTS?.[namespace.detail].threadRecommendShow.name, {
        thread_tid: threadDetail?.tid,
        thread_name: threadDetail?.subject,
        account_slug: threadDetail?.author?.id,
        thread_tag_name: extractThreadTags?.(threadDetail?.forum_types),
        list_name: (threadDetail?.forum_types || [])[0]?.name || t('btn_home'),
        is_featured: !!threadDetail?.digest,
      }, namespace.detail);
    }
  },[track,threadDetail])

  const scrollHandler = () => {
    const elem = document.getElementById("threadBlock");
    if(elem) {
      const isShow = isInViewPort(elem);
      if(isShow) {
        document.getElementById("__next")?.removeEventListener('scroll', scrollHandler);
      }
    }
  }

  useEffect(()=>{
    getThreadRelated(tid).then(res=>{
      setRelatedItems(res)
    });
    const container = document.getElementById("__next");
    container?.addEventListener('scroll', scrollHandler);
    return () => {
      container?.removeEventListener('scroll', scrollHandler);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    const container = document.getElementById("__next");
    container?.addEventListener('scroll', scrollToViewHandler);
    return () => {
      container?.removeEventListener('scroll', scrollToViewHandler);
    }
  },[scrollToViewHandler]);

  return relatedItems && relatedItems?.length > 0 ? (
    <div className={cx(Styles.root)} id="threadBlock">
      <h5 className={Styles.blockTitle}>{t("thread_related_title")}</h5>
      {
        relatedItems.map((relatedItem, index)=>{
          const device = relatedItem.author?.device && Object.keys(relatedItem.author?.device)?.length > 0 ? relatedItem.author.device : null;
          const {
            avatar,
            name,
            rank,
            total_distance,
            hide_distance,
          } = relatedItem.author || {};
          const isFirstElement = index === 0;
          return (
            <Link href={getDetailUrl(relatedItem.tid, isStoreAppBannerEntry(router.query.entry) ? true : false)} className={Styles.item} key={relatedItem.tid} onClick={()=>
              {
                // 相关推荐点击进入埋点(pc + 移动端)
                track?.(TRACK_EVENTS?.[namespace.detail].threadEntryClick.name, {
                  thread_tid: threadDetail?.tid,
                  thread_name: threadDetail?.subject,
                  account_slug: threadDetail?.author?.id,
                  thread_tag_name: extractThreadTags?.(threadDetail?.forum_types),
                  list_name: (threadDetail?.forum_types || [])[0]?.name || t('btn_home'),
                  click_thread_tid: relatedItem.tid,
                  click_thread_name: relatedItem.title,
                  click_account_slug: relatedItem.author?.id,
                  is_featured: !!threadDetail?.digest,
                  position: 'Recommend',
                }, namespace.detail);
              }
            }>
              <div className={cx(Styles.userInfo)}>
                <div className={Styles.avatar} style={{ backgroundImage: `url(${getOptimizedImageUrl(avatar, 'webp', '550') || noAvatarImageUrl})`}} />
                <div className={Styles.right}>
                  <div className={Styles.usernameAndDevices}>
                    <div className={Styles.username}>{name || t('logout_user_info_nickname')}</div>
                    {
                      rank?.vpic && (
                        <CommonImage
                          alt={rank?.vname}
                          src={require('@/assets/icon/V.png')}
                          layout="fill"
                          cutMode='cover'
                          className={Styles.rankIcon}
                        />
                      )
                    }
                    {
                      device?.icon && (
                        <CommonEquipment
                          disableClick={disableDeviceClick}
                          isLink={false}
                          name={name}
                          icon={device.icon}
                          className={Styles.device}
                        />
                      )
                    }
                  </div>
                  <div className={Styles.distance}>
                    {!hide_distance && total_distance > 0 && t('thread_total_distance', { distance: typeof total_distance === 'number' ? total_distance : 0 })}
                  </div>
                </div>
              </div>
              <ThreadTitle className={Styles.title} title={relatedItem.title} lines={1} />
              <p className={Styles.summary} ref={ isFirstElement ? domRef : undefined}>{relatedItem.summary || '一'}</p>
              <div className={Styles.bottom}>
                <div className={Styles.left}>
                  <div className={Styles.time}>{formatDateHandler(relatedItem.dateline, format)}</div>
                  { relatedItem.views && <div className={Styles.views}>{t('label_views')}<span>{formatDisplayNumber(relatedItem.views)}</span></div>}
                </div>
              </div>
            </Link>
          )
        })
      }
    </div>
  ) : null;
};

export default ThreadBlockItem;
